/**
 * Submitted theme requests, kept as a record in the chat transcript. The request itself lives in
 * the backend as a user_request; this is only the local note of "you asked for this, here is what
 * you sent", anchored to where the conversation was at the time.
 *
 * Session-scoped like the draft it comes from: a refresh keeps the receipt, a new tab starts clean.
 */

const KEY_PREFIX = "coeff.webDashboardThemeReceipts.";

/** Keeps the transcript from filling with old receipts. */
const MAX_RECEIPTS = 3;

export interface ThemeRequestReceipt {
  userRequestId: string;
  /** What was sent: "Description", the brand URL, the file name. */
  tags: string[];
  /** First line of the description, for a bit of detail at a glance. */
  summary: string;
  /** ISO timestamp of the submit. */
  submittedAt: string;
  /**
   * Id of the last chat message when the request was submitted, so the receipt renders in
   * chronological position. Null when the thread was empty.
   */
  afterMessageId: string | null;
}

const keyFor = (dashboardId?: string): string | null =>
  dashboardId ? `${KEY_PREFIX}${dashboardId}` : null;

export function readThemeRequestReceipts(dashboardId?: string): ThemeRequestReceipt[] {
  const key = keyFor(dashboardId);
  if (!key || typeof window === "undefined") {
    return [];
  }
  const raw = window.sessionStorage.getItem(key);
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ThemeRequestReceipt[]) : [];
  } catch (error) {
    if (error instanceof SyntaxError) {
      window.sessionStorage.removeItem(key);
      return [];
    }
    throw error;
  }
}

export function appendThemeRequestReceipt(
  dashboardId: string | undefined,
  receipt: ThemeRequestReceipt
): ThemeRequestReceipt[] {
  const next = [...readThemeRequestReceipts(dashboardId), receipt].slice(-MAX_RECEIPTS);
  const key = keyFor(dashboardId);
  if (key && typeof window !== "undefined") {
    window.sessionStorage.setItem(key, JSON.stringify(next));
  }
  return next;
}

export function removeThemeRequestReceipt(
  dashboardId: string | undefined,
  userRequestId: string
): ThemeRequestReceipt[] {
  const next = readThemeRequestReceipts(dashboardId).filter(r => r.userRequestId !== userRequestId);
  const key = keyFor(dashboardId);
  if (key && typeof window !== "undefined") {
    window.sessionStorage.setItem(key, JSON.stringify(next));
  }
  return next;
}
